import type { PayloadAction } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import type { Channel } from 'redux-saga';
import {
	actionChannel,
	call,
	fork,
	put,
	select,
	take,
	takeLatest,
} from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling, isAxiosError } from '../../api/errorHandling';
import {
	attachColumnsValue,
	attachDashboardValue,
} from '../../components/admin/widgetsGrids/applyTemplate';
import {
	USER_COLUMNS_PARAMETER,
	USER_CONFIG_PENDING_PARAMETER,
	USER_DASHBOARD_PARAMETER,
} from '../../components/admin/widgetsGrids/types';
import { getCookieCustom } from '../../utils/cookies';
import i18n from '../../utils/i18n';
import {
	type ClientConfigurationItem,
	getClientConfigurationSelfStart,
} from '../clientConfiguration/clientConfigurationSlice';
import { readSettingValue } from '../clientConfiguration/configValues';
import type { RootState } from '../store';
import {
	type AttachRequest,
	type ConfigUser,
	attachTemplateFinish,
	attachTemplateStart,
	attachUserResult,
	dropUserConfigRows,
	loadConfigUsersFailure,
	loadConfigUsersStart,
	loadConfigUsersSuccess,
	loadUserConfigFailure,
	loadUserConfigStart,
	loadUserConfigSuccess,
	markConfigUnsupported,
	mergeUserConfigRow,
	saveUserConfigFailure,
	saveUserConfigStart,
	saveUserConfigSuccess,
} from './userConfigurationSlice';

// A user's rows are the ones whose Name is their id. The routes below are the
// ones the app's own setup uses; there is no per-user route.
//
// Two backend builds exist. One takes Name from the body on update, so an
// admin can hand a row to another user. The other (Api main) scopes every
// route to the caller: a read of another user answers 204, and create and
// update force Name to the caller. Every write checks the Name it got back,
// so on the scoped build a save fails instead of rewriting the admin's rows.
const CONFIG_PATH = '/ClientConfiguration/v1';
// Well above any real user's row count.
const USER_ROWS_LIMIT = 500;

const updatePath = (id: string) =>
	`${CONFIG_PATH}/update/${encodeURIComponent(id)}`;
const deletePath = (id: string) =>
	`${CONFIG_PATH}/delete/${encodeURIComponent(id)}`;

type UserRowBody = Omit<ClientConfigurationItem, 'Id'>;

/** How a backend without a route answers. */
export const isUnsupported = (error: unknown): boolean =>
	isAxiosError(error) &&
	(error.response?.status === 404 || error.response?.status === 405);

const errorText = (error: unknown): string => {
	if (isAxiosError(error)) {
		const title = (error.response?.data as { title?: unknown } | undefined)
			?.title;
		return typeof title === 'string' && title ? title : error.message;
	}
	return error instanceof Error ? error.message : String(error);
};

/**
 * A 404/405 from the list route. Only a read throws it: the list route is
 * older than this feature, so a write's 404/405 is that write's own failure.
 */
class ConfigListMissingError extends Error {
	readonly original: unknown;

	constructor(original: unknown) {
		super(errorText(original));
		this.original = original;
	}
}

/** A failure this saga found itself; its message is ready for a toast. */
class ConfigSaveError extends Error {}

/**
 * A write answered with a row that is not the target user's, because the
 * backend scopes writes to the caller. This is the real sign that the
 * backend cannot hold another user's config; the list route answers on
 * every build, so a read 404/405 almost never fires.
 */
class ConfigScopedError extends ConfigSaveError {
	constructor() {
		super(i18n.t('widgetsGrids.unsupported'));
	}
}

const failedMessage = (detail: string) =>
	`${i18n.t('global.operationFailed')} (${detail})`;

const originalError = (error: unknown): unknown =>
	error instanceof ConfigListMissingError ? error.original : error;

const asRow = (data: unknown): ClientConfigurationItem | null =>
	data && typeof data === 'object' && (data as ClientConfigurationItem).Id
		? (data as ClientConfigurationItem)
		: null;

/**
 * The row a write answered with, once it is known to be the user's.
 * `withParameter` also requires the real parameter: a hand-over answered
 * with the pending one did not happen.
 */
const filedUnderUser = (
	data: unknown,
	body: UserRowBody,
	withParameter: boolean,
): ClientConfigurationItem => {
	const saved = asRow(data);
	if (!saved) {
		throw new ConfigSaveError(
			failedMessage('ClientConfiguration write returned no row'),
		);
	}
	if (
		saved.Name !== body.Name ||
		(withParameter && saved.Parameter !== body.Parameter)
	) {
		throw new ConfigScopedError();
	}
	return saved;
};

const isSelf = (userId: string) => userId === getCookieCustom('user_id');

// 204 (no rows) answers with an empty body. Rows under another Name are
// dropped: an access-group filter or a scoped backend can return them.
// Pending rows are half-made creates (see createUserRow), not settings.
const fetchUserRows = async (
	userId: string,
): Promise<ClientConfigurationItem[]> => {
	let response: AxiosResponse<unknown>;
	try {
		response = await api.post(CONFIG_PATH, {
			filters: [{ field: 'Name', operator: 'eq', value: userId }],
			limit: USER_ROWS_LIMIT,
		});
	} catch (error: unknown) {
		throw isUnsupported(error) ? new ConfigListMissingError(error) : error;
	}
	const rows = Array.isArray(response.data)
		? (response.data as ClientConfigurationItem[])
		: [];
	return rows.filter(
		(r) => r.Name === userId && r.Parameter !== USER_CONFIG_PENDING_PARAMETER,
	);
};

/** Best effort: the save has failed already, and that error is reported. */
function* discardRow(id: string) {
	try {
		yield call(api.del, deletePath(id));
	} catch {
		// A row left behind carries the pending parameter, which no read uses.
	}
}

/**
 * Best effort: puts a row the scoped build kept under the caller back under
 * the pending parameter, so that if the delete fails too, the row left
 * behind is one every read skips, not a copy of the admin's own setting.
 */
function* parkRow(id: string, data: UserRowBody) {
	try {
		yield call(api.put, updatePath(id), {
			...data,
			Parameter: USER_CONFIG_PENDING_PARAMETER,
		});
	} catch {
		// discardRow still runs.
	}
}

/**
 * `/create` ignores Name and files the row under the caller, so the row is
 * made under the pending parameter and then handed to the user by a PUT. On
 * the scoped build the PUT keeps the row under the caller as a real setting;
 * the answer shows that, and the row is parked back under the pending
 * parameter and deleted before it can pass for one of the admin's own
 * settings.
 */
function* createUserRow(data: UserRowBody) {
	const createResponse: AxiosResponse<unknown> = yield call(
		api.post,
		`${CONFIG_PATH}/create`,
		{ ...data, Parameter: USER_CONFIG_PENDING_PARAMETER },
	);
	const created = asRow(createResponse.data);
	// With no Id the row can be neither handed over nor removed, and caching
	// `Id: ''` would send the next save to PUT `/update/`.
	if (!created) {
		throw new ConfigSaveError(
			failedMessage('ClientConfiguration create returned no Id'),
		);
	}
	try {
		const response: AxiosResponse<unknown> = yield call(
			api.put,
			updatePath(created.Id),
			data,
		);
		return filedUnderUser(response.data, data, true);
	} catch (error: unknown) {
		if (error instanceof ConfigScopedError) yield* parkRow(created.Id, data);
		yield* discardRow(created.Id);
		throw error;
	}
}

/**
 * PUT the user's row for `parameter`, or create one when they have none. An
 * update answered under another Name is not deleted: the row existed before.
 */
function* writeUserSetting(
	userId: string,
	parameter: string,
	value: unknown,
	rows: ClientConfigurationItem[],
) {
	const row = rows.find((r) => r.Parameter === parameter);
	const data: UserRowBody = {
		Name: userId,
		Parameter: parameter,
		Type: 'string',
		Value: JSON.stringify(value),
	};
	if (!row) {
		const created: ClientConfigurationItem = yield call(createUserRow, data);
		return created;
	}
	const response: AxiosResponse<unknown> = yield call(
		api.put,
		updatePath(row.Id),
		data,
	);
	return filedUnderUser(response.data, data, false);
}

function* loadConfigUsers() {
	try {
		const response: AxiosResponse<unknown> = yield call(api.post, '/Users/v1', {
			limit: 1000,
		});
		const users = Array.isArray(response.data)
			? (response.data as ConfigUser[])
			: [];
		yield put(loadConfigUsersSuccess({ users }));
	} catch (error: unknown) {
		yield* errorHandling(error, loadConfigUsersFailure);
	}
}

// A write answered under another Name always means unsupported. Otherwise
// only the FIRST read answering 404/405 does: once a read has succeeded
// (supported === true), a later 404/405 is just that call's failure.
function* isNewlyUnsupported(error: unknown) {
	if (error instanceof ConfigScopedError) return true;
	if (!(error instanceof ConfigListMissingError)) return false;
	const supported: boolean | null = yield select(
		(state: RootState) => state.userConfiguration.supported,
	);
	return supported !== true;
}

// The admin's own rows also live in clientConfiguration state and the
// `configuration` cache, which this saga does not write. Re-read them so the
// admin's own screens and later /self saves see the change.
function* refreshSelfConfig() {
	yield put(getClientConfigurationSelfStart({ cached: false }));
}

function* loadUserConfig(action: PayloadAction<{ userId: string }>) {
	const { userId } = action.payload;
	try {
		const rows: ClientConfigurationItem[] = yield call(fetchUserRows, userId);
		yield put(loadUserConfigSuccess({ userId, rows }));
	} catch (error: unknown) {
		if (yield* isNewlyUnsupported(error)) {
			yield put(markConfigUnsupported());
			return;
		}
		yield* errorHandling(originalError(error), loadUserConfigFailure);
	}
}

function* saveUserConfig(
	action: PayloadAction<{ userId: string; parameter: string; value: unknown }>,
) {
	const { userId, parameter, value } = action.payload;
	let cachedRow = false;
	try {
		const known: ClientConfigurationItem[] | undefined = yield select(
			(state: RootState) => state.userConfiguration.rows[userId],
		);
		let rows: ClientConfigurationItem[] = known ?? [];
		cachedRow = rows.some((r) => r.Parameter === parameter);
		if (!cachedRow) {
			// The cache can predate a row for this parameter created elsewhere
			// (another device, or attach); writing blindly would duplicate it.
			rows = yield call(fetchUserRows, userId);
		}
		const row: ClientConfigurationItem = yield call(
			writeUserSetting,
			userId,
			parameter,
			value,
			rows,
		);
		yield put(saveUserConfigSuccess({ userId, row }));
		if (isSelf(userId)) yield* refreshSelfConfig();
	} catch (error: unknown) {
		if (yield* isNewlyUnsupported(error)) {
			yield put(markConfigUnsupported());
			// A missing list route needs no toast, the banner says it. A scoped
			// write does: the admin pressed Save and nothing landed.
			if (!(error instanceof ConfigScopedError)) return;
		}
		if (error instanceof ConfigSaveError) {
			yield put(
				saveUserConfigFailure({ message: error.message, type: 'error' }),
			);
			return;
		}
		yield* errorHandling(originalError(error), saveUserConfigFailure);
		// A 400 to the PUT of a cached row usually means the row was deleted
		// elsewhere. Re-read here rather than through loadUserConfigStart, whose
		// takeLatest would cancel a read the admin started for another user.
		// If the re-read fails, drop the cache so the next Save reads first.
		if (cachedRow && isAxiosError(error) && error.response?.status === 400) {
			try {
				const fresh: ClientConfigurationItem[] = yield call(
					fetchUserRows,
					userId,
				);
				yield put(loadUserConfigSuccess({ userId, rows: fresh }));
			} catch {
				yield put(dropUserConfigRows({ userId }));
			}
		}
	}
}

const attachTarget = (
	rows: ClientConfigurationItem[],
	request: AttachRequest,
): { parameter: string; value: unknown } =>
	request.kind === 'dashboard'
		? {
				parameter: USER_DASHBOARD_PARAMETER,
				value: attachDashboardValue(
					readSettingValue(rows, USER_DASHBOARD_PARAMETER),
					request.template,
					request.mode,
				),
			}
		: {
				parameter: USER_COLUMNS_PARAMETER,
				value: attachColumnsValue(
					readSettingValue(rows, USER_COLUMNS_PARAMETER),
					request.template,
					request.mode,
				),
			};

// One user at a time; a failure is recorded and the rest still run. Rows are
// re-read per user so the copy merges into what the user has right now.
function* attachTemplate(action: PayloadAction<AttachRequest>) {
	let wroteSelf = false;
	for (const userId of action.payload.userIds) {
		try {
			const rows: ClientConfigurationItem[] = yield call(fetchUserRows, userId);
			yield put(loadUserConfigSuccess({ userId, rows }));
			const { parameter, value } = attachTarget(rows, action.payload);
			const row: ClientConfigurationItem = yield call(
				writeUserSetting,
				userId,
				parameter,
				value,
				rows,
			);
			yield put(mergeUserConfigRow({ userId, row }));
			yield put(attachUserResult({ userId, status: 'done' }));
			if (isSelf(userId)) wroteSelf = true;
		} catch (error: unknown) {
			yield put(
				attachUserResult({
					userId,
					status: 'failed',
					error: errorText(originalError(error)),
				}),
			);
			if (yield* isNewlyUnsupported(error)) {
				yield put(markConfigUnsupported());
				break;
			}
		}
	}
	yield put(attachTemplateFinish());
	if (wroteSelf) yield* refreshSelfConfig();
}

// Writes run one at a time: two saves of one row must not both create it.
function* watchUserConfigWrites() {
	const channel: Channel<PayloadAction<unknown>> = yield actionChannel([
		saveUserConfigStart.type,
		attachTemplateStart.type,
	]);
	while (true) {
		const action: PayloadAction<unknown> = yield take(channel);
		if (saveUserConfigStart.match(action)) {
			yield call(saveUserConfig, action);
		} else if (attachTemplateStart.match(action)) {
			yield call(attachTemplate, action);
		}
	}
}

export default function* watchUserConfigurationSaga() {
	yield takeLatest(loadConfigUsersStart.type, loadConfigUsers);
	yield takeLatest(loadUserConfigStart.type, loadUserConfig);
	yield fork(watchUserConfigWrites);
}
